import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

export type TextChangeJSON = z.infer<typeof TextChangeSchema.TextChangeJSONZ>;
export type TextChangeParams = z.infer<typeof TextChangeSchema.TextChangeZ>;

export type TextChangeCollectionJSON = z.infer<typeof TextChangeSchema.TextChangeCollectionJSONZ>;
export type TextChangeCollectionParams = z.infer<typeof TextChangeSchema.TextChangeCollectionZ>;

export default class TextChangeSchema {
  public static readonly ChangeZ = z.object({
    value: z.string(),
    added: z.boolean(),
    removed: z.boolean(),
    count: z.number(),
  });

  public static readonly TextChangeJSONZ = z.object({
    diff: TextChangeSchema.ChangeZ,
    isResolved: z.boolean()
  });

  public static readonly TextChangeZ = z.object({
    diff: TextChangeSchema.ChangeZ
  });

  public static readonly TextChangeCollectionJSONZ = z.object({
    changes: z.array(TextChangeSchema.TextChangeJSONZ)
  });

  public static readonly TextChangeCollectionZ = z.object({
    changes: z.array(TextChangeSchema.TextChangeZ)
  });

  public static validate(values: unknown): TextChangeParams {
    return parseOrThrow(
      TextChangeSchema.TextChangeZ,
      values,
      { 
        logError: true,
        schemaName: "TextChangeSchema",
        operation: "validate",
        payloadSample: values
      }
    );
  }

  public static validateJSON(json: unknown): TextChangeJSON {
    return parseOrThrow(
      TextChangeSchema.TextChangeJSONZ,
      json,
      { 
        logError: true,
        schemaName: "TextChangeSchema",
        operation: "validateJSON",
        payloadSample: json
      }
    );
  }

  public static isValid(values: unknown): values is TextChangeParams {
    try {
      TextChangeSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is TextChangeJSON {
    try {
      TextChangeSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: TextChangeJSON): TextChangeParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(
      TextChangeSchema.TextChangeJSONZ,
      json,
      { 
        logError: true,
        schemaName: "TextChangeSchema",
        operation: "deserialize",
        payloadSample: json
      }
    );
    return TextChangeSchema.codec.decode(parsed);
  }

  public static serialize(params: TextChangeParams): TextChangeJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = TextChangeSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return TextChangeSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    TextChangeSchema.TextChangeJSONZ,
    TextChangeSchema.TextChangeZ, {
    decode: (data) => ({
      diff: data.diff
    }),
    encode: (change) => ({
      diff: change.diff,
      isResolved: change.diff.added && !change.diff.removed
    })
  });
}
